import React, { useEffect, useState, memo } from 'react'

interface StreamingAudioWaveProps {
  width: number
  height: number
  audioContext: AudioContext
  color?: string
  spacing?: number
  lineWidth?: number
  intervalMs?: number
  stream?: MediaStream
}

const StreamingAudioWave: React.FC<StreamingAudioWaveProps> = ({
  width,
  height,
  audioContext,
  color = '#FFFFFF',
  spacing = 2,
  lineWidth = 2,
  intervalMs = 100,
  stream = undefined,
}) => {
  const [barHeights, setBarHeights] = useState<number[]>([])
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null)
  const sampleWidth = lineWidth + spacing

  useEffect(() => {
    if (!stream) return
    setBarHeights([])
    let analyser: AnalyserNode | null = null
    let dataArray: Uint8Array | null = null
    let source: MediaStreamAudioSourceNode | null = null

    source = audioContext.createMediaStreamSource(stream)
    analyser = audioContext.createAnalyser()
    analyser.fftSize = 256
    source.connect(analyser)
    dataArray = new Uint8Array(analyser.frequencyBinCount)

    intervalRef.current = setInterval(() => {
      if (!analyser || !dataArray) return
      analyser.getByteTimeDomainData(dataArray)
      let sumSquares = 0
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] ?? 0) - 128
        sumSquares += v * v
      }
      const rms = Math.sqrt(sumSquares / dataArray.length)
      const scale = 6
      const barHeight = Math.max(2, (rms / 64) * (height / 2) * scale)
      const maxBars = Math.floor(width / sampleWidth)
      setBarHeights((prev) => {
        const next = [...prev, barHeight]
        if (next.length > maxBars) {
          next.shift()
        }
        return next
      })
    }, intervalMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (source) {
        source.disconnect()
      }
    }
  }, [stream, height, width])

  // Render live waveform (bars, like AudioWaveform)
  const renderLiveWave = (): JSX.Element => {
    const maxBars = Math.floor(width / sampleWidth)
    const barsToDraw = barHeights.slice(-maxBars)
    const n = barsToDraw.length
    return (
      <svg width={width} height={height}>
        {/* Bars, right-aligned until full, then scroll */}
        {barsToDraw.map((drawBarHeight, i) => {
          // If not full, right-align bars
          const x =
            n < maxBars ? width - (n - i) * sampleWidth : i * sampleWidth
          return (
            <g key={i}>
              {/* Erase center line under bar */}
              <rect
                x={x}
                y={height / 2 - drawBarHeight}
                width={lineWidth}
                height={drawBarHeight * 2}
                fill={color}
                rx={1}
              />
            </g>
          )
        })}
      </svg>
    )
  }

  return (
    <div
      className="w-full"
      style={{ height: height, width: width, overflow: 'hidden' }}
    >
      {renderLiveWave()}
    </div>
  )
}

export default memo(StreamingAudioWave)
